import React, { useEffect, useMemo, useRef, useState } from "react";
import client from "@/lib/api";
import { RefreshCw, MonitorPlay, Radio, AlertTriangle } from "lucide-react";

/**
 * TechnicianPreview — compact read-only camera preview for the STATION 1 tech.
 *
 * Anti-stutter contract (mirrors LivePlayer.jsx):
 *   - iframe `src` is committed ONCE per broadcast (broadcast_key = order_number:start_time).
 *   - Backend polls (every 30s) rotate the signed JWT inside `iframe_url`, but the
 *     committed `src` stays the same → no LL-HLS reload → no stutter.
 *   - Manual "Refresh Preview" is the ONLY way to force a controlled reload; it bumps
 *     a nonce that combines with `broadcast_key` to intentionally break the guard.
 *   - Preview failures are silent — they never surface a toast, never re-throw,
 *     never block the surrounding START / PAUSE / RESUME / FINISH controls.
 *   - Default muted; no user-facing volume control on this compact preview.
 */
export function TechnicianPreview({ stationId, status }) {
  const [pb, setPb] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [iframeError, setIframeError] = useState(false);
  const [lockedSrc, setLockedSrc] = useState(null);
  const committedKeyRef = useRef(null);
  const frameRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await client.get(`/stations/${stationId}/preview-playback`);
      setPb(data);
      setFetchError(false);
    } catch {
      setFetchError(true);
    }
  };

  useEffect(() => {
    load();
    // 30s poll is well within the 90s signed-token TTL and cheap for a health-check panel.
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  // Broadcast is playable through the full technician-owned lifecycle:
  // LIVE → PAUSED → LIVE → TESTING. Cloudflare keeps producing signal for the
  // same station Live Input UID during TESTING (backend
  // `/stations/{id}/preview-playback` returns the same signed URL for all
  // three statuses), so the preview must stay mounted — otherwise the iframe
  // gets torn down at FINISH ASSEMBLY and re-shows "CAMERA OFFLINE".
  const isActiveBroadcast = status === "LIVE" || status === "PAUSED" || status === "TESTING";
  const canShow = isActiveBroadcast && !!pb?.iframe_url;
  const broadcastKey = pb?.broadcast_key || "";

  // Commit `src` exactly once per (broadcast_key, nonce) pair. The nonce only
  // ever changes when the tech clicks Refresh Preview.
  useEffect(() => {
    if (!canShow) {
      // Reset when we lose signal so a fresh broadcast rebuilds cleanly.
      committedKeyRef.current = null;
      setLockedSrc(null);
      setIframeError(false);
      return;
    }
    const key = `${broadcastKey}::${nonce}`;
    if (committedKeyRef.current !== key) {
      committedKeyRef.current = key;
      setLockedSrc(`${pb.iframe_url}?autoplay=true&muted=true&primaryColor=%2300e5ff`);
      setIframeError(false);
    }
    // Intentional: we depend on iframe_url so JWT-only churn re-enters the effect,
    // but the guard above rejects the commit if the key hasn't changed.
  }, [canShow, broadcastKey, nonce, pb?.iframe_url]);

  const onManualRefresh = async () => {
    await load();
    setNonce((n) => n + 1);
  };

  const badge = useMemo(() => {
    if (canShow && lockedSrc && !iframeError && !fetchError) {
      return { label: "LIVE PREVIEW", color: "#00e5ff", bg: "rgba(0,229,255,0.08)", border: "rgba(0,229,255,0.4)", pulse: true };
    }
    // Status LIVE/PAUSED/TESTING but no playable stream (cloudflare_pending, signing_error, iframe error, or fetch error)
    // → the build is running but the camera isn't producing signal.
    if (isActiveBroadcast && (!canShow || iframeError || fetchError)) {
      return { label: "NO SIGNAL", color: "#ff9800", bg: "rgba(255,152,0,0.08)", border: "rgba(255,152,0,0.45)", pulse: false };
    }
    if (canShow && !lockedSrc) {
      return { label: "CONNECTING…", color: "#a17dff", bg: "rgba(161,125,255,0.08)", border: "rgba(161,125,255,0.4)", pulse: false };
    }
    return { label: "CAMERA OFFLINE", color: "#8a8a94", bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.12)", pulse: false };
  }, [canShow, lockedSrc, iframeError, fetchError, status, isActiveBroadcast]);

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }}
      data-testid="tech-preview-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Radio size={14} style={{ color: badge.color }} />
          <span className="text-[10px] tracking-[0.28em] uppercase text-white/60 font-semibold">Camera Preview</span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-semibold tracking-[0.18em] border"
            style={{ color: badge.color, background: badge.bg, borderColor: badge.border }}
            data-testid="tech-preview-status-badge"
          >
            {badge.pulse && (
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: badge.color, boxShadow: `0 0 6px ${badge.color}` }}
              />
            )}
            {badge.label}
          </span>
          <button
            type="button"
            onClick={onManualRefresh}
            className="w-7 h-7 rounded-md border flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            style={{ borderColor: "rgba(255,255,255,0.12)" }}
            title="Refresh Preview"
            data-testid="tech-preview-refresh"
          >
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      <div
        className="relative w-full overflow-hidden rounded-lg border"
        style={{
          aspectRatio: "16 / 9",
          maxHeight: 200,
          borderColor: canShow ? "rgba(0,229,255,0.25)" : "rgba(255,255,255,0.08)",
          background: "#0a0a0d",
        }}
        data-testid="tech-preview-frame-wrap"
      >
        {canShow && lockedSrc && !iframeError ? (
          <iframe
            ref={frameRef}
            src={lockedSrc}
            className="w-full h-full"
            allow="autoplay; encrypted-media; picture-in-picture;"
            title="Technician Camera Preview"
            onError={() => setIframeError(true)}
            data-testid="tech-preview-iframe"
          />
        ) : (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-4"
            style={{
              background: "radial-gradient(circle at 50% 45%, rgba(0,229,255,0.06), rgba(0,0,0,0) 55%), #0a0a0d",
            }}
            data-testid="tech-preview-empty"
          >
            {isActiveBroadcast && !canShow ? (
              <AlertTriangle size={22} style={{ color: "#ff9800" }} />
            ) : (
              <MonitorPlay size={22} style={{ color: canShow ? "var(--accent)" : "rgba(255,255,255,0.35)" }} />
            )}
            <div className="mt-2 text-white/70 text-[11px] font-semibold tracking-wide">
              {status === "WAITING"
                ? "AWAITING BUILD START"
                : status === "COMPLETED"
                ? "PREVIEW ENDED"
                : isActiveBroadcast && !canShow
                ? "NO SIGNAL FROM CAMERA"
                : canShow
                ? "CONNECTING…"
                : "CAMERA OFFLINE"}
            </div>
            <div className="mt-1 text-white/40 text-[10px] leading-snug max-w-[260px]">
              {status === "WAITING"
                ? "The preview starts the moment the build starts."
                : isActiveBroadcast && !canShow
                ? "OBS/encoder isn't sending yet. Check your ingest, then click refresh."
                : "Audio is muted by default. Click refresh if the picture stalls."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
